import type { FieldValues, UseFormReturn } from "react-hook-form";
import type { FormValidationFailureHandler, FormValidator } from "./types";

/**
 * Walks the RHF errors tree and returns the dotted path of every leaf error.
 * Used to mark errored fields as touched after trigger() so Form* components
 * (which gate inline error display on fieldState.isTouched) render the
 * validation messages without requiring the user to blur each field first.
 *
 * Leaf detection: a node is a FieldError when it has a string `type` or
 * `message` property — RHF's documented FieldError shape.
 */
export function flattenErrorPaths(errors: unknown, prefix = ""): string[] {
  if (!errors || typeof errors !== "object") return [];
  const obj = errors as Record<string, unknown>;
  if (typeof obj.type === "string" || typeof obj.message === "string") {
    return prefix ? [prefix] : [];
  }
  const paths: string[] = [];
  for (const key of Object.keys(obj)) {
    if (key === "root") continue;
    const childPath = prefix ? `${prefix}.${key}` : key;
    paths.push(...flattenErrorPaths(obj[key], childPath));
  }
  return paths;
}

/**
 * Runs field-level validation (via trigger) and optional form-level validate,
 * surfaces form-level errors as field errors, marks errored fields touched for
 * inline display, and focuses the first invalid field.
 */
export async function validateBeforeSubmit<TValues extends FieldValues>(
  methods: UseFormReturn<TValues>,
  validate?: FormValidator<TValues>,
  onValidationFailure?: FormValidationFailureHandler<TValues>,
): Promise<boolean> {
  const { trigger, getValues, setError, setFocus } = methods;

  const rhfValid = await trigger();

  const formErrors = validate ? validate(getValues() as TValues) : {};
  const formErrorEntries = Object.entries(formErrors).filter(([, v]) => !!v);

  formErrorEntries.forEach(([name, message]) => {
    setError(name as never, {
      type: "manual",
      message: String(message),
    });
  });

  if (!rhfValid || formErrorEntries.length > 0) {
    const errorPaths = Array.from(
      new Set([
        ...flattenErrorPaths(methods.formState.errors),
        ...formErrorEntries.map(([name]) => name),
      ]),
    );
    onValidationFailure?.(errorPaths, getValues() as TValues);
    errorPaths.forEach(path => {
      const current = methods.getValues(path as never);
      methods.setValue(path as never, current as never, {
        shouldTouch: true,
        shouldDirty: false,
        shouldValidate: false,
      });
    });

    const firstError =
      errorPaths[0] ??
      Object.keys(methods.formState.errors)[0] ??
      formErrorEntries[0]?.[0];
    if (firstError) {
      try {
        setFocus(firstError as never);
      } catch {
        // setFocus throws if the field isn't a registered DOM input (e.g.
        // HiddenValidatedField, virtual fields). Inline errors still render.
      }
    }
    return false;
  }

  return true;
}
